import * as React from "react";
import { DEFAULT_TZ, IClient } from "intake_shared/src/types";
import { TSaveClient } from "../../../../clientUiTypes";
import { useToasts } from "react-toast-notifications";
import { AclContext } from "../../../../../../../contexts/AclContext";
import moment from "../../../../../../../utils/app_moment";
import { generate_uuid } from "../../../../../../../utils/utils";
import { createNewClient } from "../../../../../../../store/client";
import { Button } from "../../../../../bits/Button/Button";
import CopyIcon from "../../../../../icons/Copy";
import { Input } from "../../../../../bits/Input/Input";
import { DatePicker } from "../../../../../bits/DatePicker/DatePicker";
import { Dialog } from "../../../../../bits/Dialog/Dialog";

interface DuplicateChartProps {
    client: IClient;
    onSaveClient: TSaveClient;
}

export default function DuplicateChart({
    client,
    onSaveClient,
}: DuplicateChartProps) {
    const { addToast } = useToasts();
    const [selectedDuplicate, setSelectedDuplicate] = React.useState(false);
    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;
    const momentDob = client?.date_of_birth ? moment(client.date_of_birth) : null;
    const [updateDuplicateDetails, setUpdateDuplicateDetails] = React.useState({
        first_name: client.first_name,
        last_name: client.last_name,
        email: client.email,
        preferred_name: client.preferred_name,
    });
    const [dateOfBirth, setDateOfBirth] = React.useState<Date | null>(
        momentDob ? momentDob.toDate() : null
    );
    const [canDuplicate, setCanDuplicate] = React.useState(false);

    React.useEffect(() => {
        const isEmailUpdated = updateDuplicateDetails.email !== client.email;
        const isDobUpdated =
            dateOfBirth?.getTime() !== momentDob?.toDate().getTime();
        setCanDuplicate(isEmailUpdated || isDobUpdated);
    }, [updateDuplicateDetails.email, dateOfBirth, client.email, momentDob]);

    const handleDuplicateClient = async () => {
        const dob = dateOfBirth
            ? moment.tz(dateOfBirth?.toISOString(), DEFAULT_TZ).hour(12)
            : null;
        const dob_date = dob ? dob?.toDate() : null;

        const id = generate_uuid(
            `${updateDuplicateDetails.email}${dob_date?.getTime()}`
        );

        const newClient: IClient = {
            ...client,
            id,
            email: updateDuplicateDetails.email,
            first_name: updateDuplicateDetails.first_name.trim(),
            preferred_name: updateDuplicateDetails.preferred_name.trim(),
            last_name: updateDuplicateDetails.last_name.trim(),
            date_of_birth: dob ? dob.toDate() : null,
            confirmed_bookings: [],
            held_bookings: [],
            appointment_history: [],
        };
        try {
            addToast("Duplicating, please wait....", {
                appearance: "success",
            });
            await createNewClient(newClient, loggedInUserEmail as string);
            addToast("Client duplicated successfully", {
                appearance: "success",
            });
            onSaveClient(newClient);
            setSelectedDuplicate(false);
        } catch (err) {
            addToast("Client could not be duplicated", {
                appearance: "error",
            });
        }
    };

    return (
        <>
            <Button
                mode="outline"
                onClick={() => setSelectedDuplicate(true)}
                iconLeft={<CopyIcon stroke="#1A738D" />}
            >
                Duplicate chart
            </Button>
            <Dialog
                open={selectedDuplicate}
                onClose={() => setSelectedDuplicate(false)}
                onSave={handleDuplicateClient}
                title="Duplicate chart"
                onCancel={() => setSelectedDuplicate(false)}
                saveText="Duplicate"
                isSubmitBtnDisabled={!canDuplicate}
            >
                <div className="v2-form">
                    <p>
                        Are you sure you want to duplicate this chart? You must
                        change either the email or date of birth to proceed.
                    </p>
                    <div className="gridTwo">
                        <Input
                            label="First name"
                            onChange={(e) => {
                                setUpdateDuplicateDetails({
                                    ...updateDuplicateDetails,
                                    first_name: e.target.value,
                                });
                            }}
                            value={updateDuplicateDetails.first_name}
                        />
                        <Input
                            label="Last name"
                            onChange={(e) => {
                                setUpdateDuplicateDetails({
                                    ...updateDuplicateDetails,
                                    last_name: e.target.value,
                                });
                            }}
                            value={updateDuplicateDetails.last_name}
                        />
                    </div>
                    <div className="gridTwo">
                        <Input
                            label="Preferred name"
                            value={updateDuplicateDetails.preferred_name}
                            onChange={(e) => {
                                setUpdateDuplicateDetails({
                                    ...updateDuplicateDetails,
                                    preferred_name: e.target.value,
                                });
                            }}
                        />
                        <Input
                            type="email"
                            label="Email Address"
                            onChange={(e) => {
                                setUpdateDuplicateDetails({
                                    ...updateDuplicateDetails,
                                    email: e.target.value,
                                });
                            }}
                            value={updateDuplicateDetails.email}
                        />
                    </div>
                    <div className="gridTwo">
                        <DatePicker
                            label="Date of birth"
                            dateFormat={"MM/dd/yyyy"}
                            selected={dateOfBirth}
                            onChange={(date) => {
                                setDateOfBirth(date as Date);
                            }}
                            popperPlacement="top"
                        />
                    </div>
                </div>
            </Dialog>
        </>
    );
}
